import { createRouter, createWebHistory } from "vue-router";
import { ElMessage } from "element-plus";
import { useAuthStore } from "@/stores/auth";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      name: "home",
      component: () => import("@/views/HomeView.vue"),
      meta: { title: "Home" },
    },
    {
      path: "/login",
      name: "login",
      component: () => import("@/views/LoginView.vue"),
      meta: { title: "Login", guest: true },
    },
    {
      path: "/register",
      name: "register",
      component: () => import("@/views/RegisterView.vue"),
      meta: { title: "Register", guest: true },
    },
    {
      path: "/forgot-password",
      name: "forgot-password",
      component: () => import("@/views/ForgotPasswordView.vue"),
      meta: { title: "Forgot Password", guest: true },
    },
    {
      path: "/verify-email",
      name: "verify-email",
      component: () => import("@/views/EmailVerificationView.vue"),
      meta: { title: "Email Verification" },
    },
    {
      path: "/universities",
      name: "universities",
      component: () => import("@/views/UniversitiesView.vue"),
      meta: { title: "Universities" },
    },
    {
      path: "/courses",
      name: "courses",
      component: () => import("@/views/CoursesView.vue"),
      meta: { title: "Courses" },
    },
    {
      path: "/courses/:id",
      name: "course-detail",
      component: () => import("@/views/CourseDetailView.vue"),
      meta: { title: "Course Detail" },
    },
    {
      path: "/add-course",
      name: "add-course",
      component: () => import("@/views/AddCourseView.vue"),
      meta: { title: "Add Course", requiresAuth: true },
    },
    {
      path: "/reviews",
      name: "reviews",
      component: () => import("@/views/ReviewsView.vue"),
      meta: { title: "My Reviews", requiresAuth: true },
    },
    {
      path: "/profile",
      name: "profile",
      component: () => import("@/views/ProfileView.vue"),
      meta: { title: "Profile", requiresAuth: true },
    },
    {
      path: "/bookmarks",
      name: "bookmarks",
      component: () => import("@/views/BookmarksView.vue"),
      meta: { title: "My Bookmarks", requiresAuth: true },
    },
    {
      path: "/follows",
      name: "follows",
      component: () => import("@/views/FollowsView.vue"),
      meta: { title: "Follows", requiresAuth: true },
    },
    {
      path: "/messages",
      name: "messages",
      component: () => import("@/views/MessagesView.vue"),
      meta: { title: "Messages", requiresAuth: true },
    },
    {
      path: "/users/:id",
      name: "user-profile",
      component: () => import("@/views/UserProfileView.vue"),
      meta: { title: "User Profile" },
    },
    {
      path: "/trending",
      name: "trending",
      component: () => import("@/views/TrendingView.vue"),
      meta: { title: "Trending" },
    },
    {
      path: "/stats",
      redirect: "/trending",
    },
    {
      path: "/about",
      name: "about",
      component: () => import("@/views/AboutView.vue"),
      meta: { title: "About Us" },
    },
    {
      path: "/recommendations",
      name: "recommendations",
      component: () => import("@/views/RecommendationsView.vue"),
      meta: { title: "Smart Recommendations" },
    },
    {
      path: "/access-test",
      name: "access-test",
      component: () => import("@/views/AccessTestView.vue"),
      meta: { title: "Access Control Test" },
    },
    {
      path: "/:pathMatch(.*)*",
      name: "not-found",
      component: () => import("@/views/NotFoundView.vue"),
      meta: { title: "Page Not Found" },
    },
  ],
});

// Route guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // Wait for auth initialization before checking authentication status
  if (!authStore.initialized) {
    try {
      await authStore.initializeAuth();
    } catch (err) {
      console.error('Auth initialization failed:', err);
      // Continue navigation even if initialization fails
    }
  }

  // Set page title
  document.title = `${to.meta.title} - KursKompass`;

  // Pages requiring authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    ElMessage.warning("Please login to access this page");
    next({ path: "/login", query: { redirect: to.fullPath } });
    return;
  }

  // Guest pages (logged in users cannot access)
  if (to.meta.guest && authStore.isAuthenticated) {
    ElMessage.info("You are already logged in");
    next("/");
    return;
  }

  next();
});

export default router;
